"use client";

// BUG-11-CHG-01: stays on raw wallet state (useWallet Zustand store, which reflects actual connected wallet). Connection UI must reflect actual wallet, never effective. See arandao-docs/architecture/impersonation-ux.md §Integration Points exempt list.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/auth";
import { useWallet } from "@/context/wallet";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";

export const ConnectButton = ({ children }: { children: React.ReactNode }) => {
  const { isConnected, open, ready, fullConfigReady, fullConfigFailed } =
    useWallet();
  const { handleContractError } = useContractErrorHandler();
  const setHasExplicitlyLoggedOut = useAuth((s) => s.setHasExplicitlyLoggedOut);
  const [isLoading, setIsLoading] = useState(false);

  // A connected wallet has no use for a connect button — allowlisted or not,
  // this is the only hiding rule the component needs.
  if (isConnected) return null;

  // Deliberately NOT gated on maintenance mode. Hiding this during a freeze
  // made the maintenance allowlist unreachable: a wallet cannot be checked
  // against the list until it connects, so anyone whose session expired, who
  // cleared storage, or who is on a new device was locked out of their own
  // exemption with no way to earn it back.
  //
  // Non-allowlisted users are still stopped — the backend refuses their login
  // with 503 (MaintenanceGuard) — just one step later than before. Trading a
  // failed sign for "the people we invited can actually get in" is the point
  // of the allowlist. See useMaintenanceBypass for the read side.

  // During the minimal→full wagmi config swap, useAccount briefly reports
  // isConnected=false even when the user has a persisted connection (only
  // the injected connector is in the minimal config). Render a loading
  // shell instead of an active "connect" button until the full config
  // mounts; otherwise users see "اتصال" then watch it disappear.
  const swapInFlight = !fullConfigReady && !fullConfigFailed;

  const handleClick = async () => {
    // Clear the persisted logout flag — the user is explicitly trying to connect,
    // so any stale "logged out" state from a previous cancel/disconnect should not block them.
    setHasExplicitlyLoggedOut(false);
    setIsLoading(true);
    try {
      await open();
    } catch (error) {
      handleContractError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      size="md"
      className="px-8"
      colors="primary"
      loading={isLoading || !ready || swapInFlight}
      disabled={!ready || swapInFlight}
      onClick={handleClick}
    >
      {children}
    </Button>
  );
};
